// Tracing utilities for the two-hand drawing app.
//
// The "two hand" requirement: progress on a side only counts while BOTH sides
// are actively being traced at the same moment. This module captures the
// gating rule in a pure function so it can be unit tested independently of the
// React component.

export function distance(a, b) {
  const dx = b.x - a.x
  const dy = b.y - a.y
  return Math.sqrt(dx * dx + dy * dy)
}

/**
 * Shortest distance from `point` to the line segment between `segA` and `segB`.
 */
export function pointToSegmentDistance(point, segA, segB) {
  const dx = segB.x - segA.x
  const dy = segB.y - segA.y
  const lengthSquared = dx * dx + dy * dy
  if (lengthSquared === 0) return distance(point, segA)
  let t = ((point.x - segA.x) * dx + (point.y - segA.y) * dy) / lengthSquared
  t = Math.max(0, Math.min(1, t))
  return distance(point, {
    x: segA.x + t * dx,
    y: segA.y + t * dy,
  })
}

export const CHECKPOINT_RADIUS = 18

/**
 * Advance the cleared-checkpoint cursor for a single side based on a new
 * pointer sample. Checkpoints must be cleared sequentially.
 *
 * When `prevSample` is provided we check the line segment from the previous
 * sample to the current one against the next checkpoint, so a fast drag that
 * skips past a corner between two samples still counts.
 *
 * @param {Array<{x:number,y:number}>} path - The shape's checkpoint path.
 * @param {number} clearedCount - How many checkpoints have been cleared so far.
 * @param {{x:number,y:number}} sample - The latest pointer sample for this side.
 * @param {number} [threshold=CHECKPOINT_RADIUS]
 * @param {{x:number,y:number}|null} [prevSample=null] - Optional prior sample.
 * @returns {number} the new cleared count.
 */
export function advanceCheckpoints(
  path,
  clearedCount,
  sample,
  threshold = CHECKPOINT_RADIUS,
  prevSample = null
) {
  if (!path || path.length === 0) return clearedCount
  let cleared = clearedCount
  while (cleared < path.length) {
    const next = path[cleared]
    const d = prevSample
      ? pointToSegmentDistance(next, prevSample, sample)
      : distance(sample, next)
    if (d <= threshold) {
      cleared += 1
    } else {
      break
    }
  }
  return cleared
}

/**
 * Gate the progression: a side only progresses while the other side is also
 * being actively traced. Velocity can differ, but both sides must be drawing
 * at the same moment.
 *
 * Returns the new {left, right} cleared counts.
 */
export function applySimultaneousProgress({
  leftPath,
  rightPath,
  leftCleared,
  rightCleared,
  leftSample,
  rightSample,
  leftPrevSample = null,
  rightPrevSample = null,
  leftActive,
  rightActive,
  threshold = CHECKPOINT_RADIUS,
}) {
  if (!leftActive || !rightActive) {
    return { left: leftCleared, right: rightCleared }
  }
  return {
    left:
      leftSample == null
        ? leftCleared
        : advanceCheckpoints(leftPath, leftCleared, leftSample, threshold, leftPrevSample),
    right:
      rightSample == null
        ? rightCleared
        : advanceCheckpoints(rightPath, rightCleared, rightSample, threshold, rightPrevSample),
  }
}

/**
 * Both sides have cleared every checkpoint on their respective shape.
 */
export function isComplete(leftPath, rightPath, leftCleared, rightCleared) {
  if (!leftPath?.length || !rightPath?.length) return false
  return leftCleared >= leftPath.length && rightCleared >= rightPath.length
}
